/**
 * src/pages/Actividad.jsx
 * Actividad del sistema: runs del pipeline y accesos (logins) en el tiempo.
 * Rangos semana/mes/año; clic en una barra abre el detalle del día.
 */
import { useMemo, useState } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { Header } from '../components/layout/Header'
import { Card, KpiCard } from '../components/ui/Card'
import { Badge, RESULTADO_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { useActividad, useActividadDia } from '../hooks/useAdminPanel'

const RANGOS = [
  { dias: 7, label: 'Semana' },
  { dias: 30, label: 'Mes' },
  { dias: 365, label: 'Año' },
]

function agruparPorMes(serie) {
  const meses = new Map()
  for (const p of serie) {
    const mes = p.fecha.slice(0, 7)
    const m = meses.get(mes) || { fecha: mes, runs_ok: 0, runs_error: 0, accesos: 0 }
    m.runs_ok += p.runs_ok
    m.runs_error += p.runs_error
    m.accesos += p.accesos
    meses.set(mes, m)
  }
  return [...meses.values()]
}

function etiqueta(fecha, dias) {
  if (dias === 365) {
    const [y, m] = fecha.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('es-CL', { month: 'short' })
  }
  const [y, m, d] = fecha.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return dias === 7
    ? dt.toLocaleDateString('es-CL', { weekday: 'short' })
    : `${d}/${m}`
}

const RUN_COLUMNS = [
  { key: 'job_id', label: 'Job', mono: true, width: '70px', render: v => `#${v}` },
  { key: 'cliente_id', label: 'Cliente', mono: true },
  { key: 'tipo', label: 'Tipo', mono: true, width: '90px' },
  {
    key: 'estado', label: 'Estado', width: '90px',
    render: v => <Badge color={RESULTADO_COLOR[v] || 'gray'}>{v}</Badge>,
  },
  {
    key: 'duracion_min', label: 'Duración', mono: true, align: 'right', width: '90px',
    render: v => v != null ? `${v} min` : '—',
  },
]

const ACCESO_COLUMNS = [
  { key: 'cliente_id', label: 'Cliente', mono: true, render: v => v || '(admin)' },
  { key: 'rol', label: 'Rol', mono: true, width: '90px' },
  { key: 'origen', label: 'Origen', mono: true, width: '110px' },
  { key: 'n', label: 'Accesos', mono: true, align: 'right', width: '80px' },
]

function DetalleDia({ fecha, onClose }) {
  const { data, isLoading } = useActividadDia(fecha)
  return (
    <Card
      title={`Detalle del ${fecha}`}
      subtitle={data ? `${data.total_runs} runs · ${data.total_accesos} accesos` : 'Cargando…'}
      padding="0"
      style={{ marginTop: '14px' }}
      action={
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--color-text-3)', fontSize: '16px', lineHeight: 1,
        }}>×</button>
      }
    >
      <div style={{
        padding: '12px 20px 4px', fontSize: '11px', color: 'var(--color-text-3)',
        fontFamily: 'var(--font-mono)', letterSpacing: '0.08em',
      }}>
        RUNS DEL PIPELINE
      </div>
      <Table columns={RUN_COLUMNS} data={data?.runs} loading={isLoading}
             emptyMessage="Sin runs este día" />
      <div style={{
        padding: '12px 20px 4px', fontSize: '11px', color: 'var(--color-text-3)',
        fontFamily: 'var(--font-mono)', letterSpacing: '0.08em',
        borderTop: '1px solid var(--color-border)',
      }}>
        ACCESOS
      </div>
      <Table columns={ACCESO_COLUMNS} data={data?.accesos} loading={isLoading}
             emptyMessage="Sin accesos este día" />
    </Card>
  )
}

export default function Actividad() {
  const [dias, setDias] = useState(7)
  const [diaSel, setDiaSel] = useState(null)
  const { data, isLoading } = useActividad(dias)

  function abrirDia(d) {
    const fecha = d?.payload?.fecha ?? d?.fecha
    if (fecha && dias !== 365) setDiaSel(fecha)
  }

  const serie = useMemo(() => {
    const s = data?.serie || []
    const agregada = dias === 365 ? agruparPorMes(s) : s
    return agregada.map(p => ({ ...p, label: etiqueta(p.fecha, dias) }))
  }, [data, dias])

  const totales = useMemo(() => serie.reduce(
    (t, p) => ({
      ok: t.ok + p.runs_ok, error: t.error + p.runs_error, accesos: t.accesos + p.accesos,
    }),
    { ok: 0, error: 0, accesos: 0 },
  ), [serie])

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Actividad"
        subtitle="Runs del pipeline y accesos al panel/dashboard"
        action={
          <div style={{ display: 'flex', gap: '4px' }}>
            {RANGOS.map(r => (
              <button
                key={r.dias}
                onClick={() => { setDias(r.dias); setDiaSel(null) }}
                style={{
                  border: '1px solid var(--color-border)',
                  background: dias === r.dias ? 'var(--color-accent-dim)' : 'var(--color-surface)',
                  color: dias === r.dias ? 'var(--color-accent)' : 'var(--color-text-2)',
                  borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
                  fontSize: '12px', fontFamily: 'var(--font-mono)',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px',
          marginBottom: '24px',
        }}>
          <KpiCard label="RUNS OK" value={isLoading ? '—' : totales.ok}
                   color="var(--color-ok)" sub={`últimos ${dias} días`} />
          <KpiCard label="RUNS CON ERROR" value={isLoading ? '—' : totales.error}
                   color={totales.error > 0 ? 'var(--color-error)' : 'var(--color-text-3)'}
                   sub={`últimos ${dias} días`} />
          <KpiCard label="ACCESOS" value={isLoading ? '—' : totales.accesos}
                   color="var(--color-running)" sub="logins panel + dashboard" />
        </div>

        <Card
          title="Evolución"
          subtitle={dias === 365 ? 'Agregado mensual · clic en una barra para el detalle' : 'Diario · clic en una barra para el detalle del día'}
        >
          {serie.length === 0 && !isLoading ? (
            <div style={{
              textAlign: 'center', padding: '32px', color: 'var(--color-text-3)',
              fontSize: '12px', fontFamily: 'var(--font-mono)',
            }}>
              Sin actividad en el rango
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={serie}
                margin={{ top: 4, right: 0, left: -20, bottom: 0 }}
              >
                <XAxis dataKey="label"
                  tick={{ fill: 'var(--color-text-3)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
                  axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false}
                  tick={{ fill: 'var(--color-text-3)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
                  axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                    borderRadius: '6px', fontSize: '12px', fontFamily: 'var(--font-mono)',
                    color: 'var(--color-text)',
                  }}
                  cursor={{ fill: 'rgba(16,185,129,0.05)' }}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fecha || ''}
                />
                <Legend wrapperStyle={{ fontSize: '11px', fontFamily: 'var(--font-mono)' }} />
                {/* onClick por barra: recibe su propio payload (estable en recharts v3,
                    a diferencia del activePayload del onClick a nivel de gráfico) */}
                <Bar dataKey="runs_ok" name="Runs OK" stackId="runs"
                     fill="var(--color-ok)" radius={[0, 0, 0, 0]} cursor="pointer"
                     onClick={d => abrirDia(d)} />
                <Bar dataKey="runs_error" name="Runs error" stackId="runs"
                     fill="var(--color-error)" radius={[3, 3, 0, 0]} cursor="pointer"
                     onClick={d => abrirDia(d)} />
                <Bar dataKey="accesos" name="Accesos"
                     fill="var(--color-running)" radius={[3, 3, 0, 0]} cursor="pointer"
                     onClick={d => abrirDia(d)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {diaSel && <DetalleDia fecha={diaSel} onClose={() => setDiaSel(null)} />}
      </div>
    </div>
  )
}
